import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import PlayerForm from '../components/player.form';
import PlayerSummerTourForm from '../components/player.summertour.form';
import ThankYou from '../views/thankyou';
import PlayerList from '../components/player.list';
import DetailsForm from '../components/details.form';
import ValidationFeedback from '../components/validation.feedback';
import GetMemberForm from '../components/get-member.form';
import PlayerSelect from '../components/player.select';
import { isEmailValid, isGolfIdValid, isHandicapValid, isPostCodeValid } from '../util/validator';
import { sortPlayers } from '../util/sort';

import GoogleSheetsService from '../service/googlesheet.service';

const RegistrationForm = ({ competitionType, reCAPTCHAActive, goBack, resetRegistrationType }) => {
    const [loadingCompetitions, setLoadingCompetitions] = useState(true);
    const [competitions, setCompetitions] = useState([]);
    const [competition, setCompetition] = useState({ id: '' });
    const [member, setMember] = useState({});
    const [players, setPlayers] = useState([]);
    const [registeredPlayers, setRegisteredPlayers] = useState([]);
    const [playerCount, setPlayerCount] = useState(0);
    const [validationError, setValidationError] = useState('');
    const [validationWarning, setValidationWarning] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showThankYou, setShowThankYou] = useState(0);
    const [approveReCAPTCHA, setApproveReCAPTCHA] = useState(false);
    const [approveGDPR, setApproveGDPR] = useState(false);
    const [singleRegistration, setSingleRegistration] = useState(false);
    const [registrationDetails, setRegistrationDetails] = useState({
        email: '',
        telephone: '',
        postCode: '',
        city: ''
    });

    const recaptchaRef = useRef();
    const infoTitleRef = useRef();
    const memberSearchRef = useRef();
    const thankYouRef = useRef();
    const playersRef = useRef();
    const submitButtonRef = useRef();
    const competitionRef = useRef();

    useEffect(() => {
        if (showThankYou === 0) {
            setCompetition({ id: '' });
            GoogleSheetsService.getCompetitions(competitionType).then((result) => {
                if (competitionRef?.current !== undefined && competitionRef?.current !== null) {
                    setCompetitions(result ?? []);
                    setLoadingCompetitions(false);
                }
            });
        } else {
            thankYouRef.current.scrollIntoView({
                behavior: "smooth",
            });
        }
    }, [competitionType, showThankYou]);

    useEffect(() => {
        if (memberSearchRef?.current !== undefined && memberSearchRef?.current !== null) {
            memberSearchRef.current.scrollIntoView({
                behavior: "smooth",
            });
        }
    }, [competition]);

    useEffect(() => {
        if (competitionType === 2 && infoTitleRef?.current !== undefined && infoTitleRef?.current !== null && playerCount === 0) {
            infoTitleRef.current.scrollIntoView({
                behavior: "smooth",
            });
        } else if (submitButtonRef?.current !== undefined && submitButtonRef?.current !== null && players.filter(p => p.participating).length > playerCount) {
            submitButtonRef.current.scrollIntoView({
                behavior: "smooth",
            });
        } else if (playersRef?.current !== undefined && playersRef?.current !== null && playerCount === 0) {
            playersRef.current.scrollIntoView({
                behavior: "smooth",
            });
        }
        setPlayerCount(players.filter(p => p.participating).length);
    }, [competitionType, players, playerCount]);

    useEffect(() => {
        if (validationError && validationError !== '') {
            setTimeout(() => setValidationError(''), 10000)
        }
    }, [validationError]);

    useEffect(() => {
        if (competition.noOfOpenSpots < players.filter(_ => _.participating === true && _.alreadyRegistered === false).length) {
            setValidationWarning('Det finns inte tillräckligt med platser på tävlingen för alla spelare som du har valt, alla spelare kommer bli reserver');
        } else {
            setValidationWarning('');
        }
    }, [players, competition.noOfOpenSpots, registeredPlayers]);

    const updatePlayers = (updatedPlayers) => {
        updatedPlayers.map(p => {
            p.memberNo = member.number;
            p.alreadyRegistered = isPlayerRegistered(p);
            return p;
        });
        setPlayers(updatedPlayers);
    }

    const isPlayerRegistered = (player) => {
        if (competitionType === 1) {
            return registeredPlayers.filter(_ => _.memberNo === member.number && _.name.toLowerCase().trim() === player.name.toLowerCase().trim()).length > 0;
        } else {
            return registeredPlayers.filter(_ => _.golfId === player.golfId && _.name.toLowerCase().trim() === player.name.toLowerCase().trim()).length > 0;
        }
    }

    const updateMember = (member) => {
        if (member) {
            setPlayers([]);
            setMember(member);
            setRegistrationDetails({
                email: member.email,
                telephone: member.phoneNumber,
                postCode: member.postCode,
                city: member.city
            });
            member.players.map(p => {
                p.teeType = '';
                p.isGuest = false;
                p.dinnerOption = '';
                p.id = p.name;
                return p;
            });
            member.players = member.players.sort((a, b) => sortPlayers(a, b));
            setPlayers(member.players);
        } else {
            if (competitionType === 2) {
                setDefaultSummerTourPlayers();
            } else {
                setPlayers([]);
            }
            setRegistrationDetails({
                email: '',
                telephone: '',
                postCode: '',
                city: ''
            });
            setMember({});
        }
    }

    const setDefaultSummerTourPlayers = () => {
        let summerTourPlayers = [
            {
                id: 'summer-tour-player-1',
                name: '',
                golfId: '',
                teeType: '',
                dinnerOption: '',
                participating: true,
                alreadyRegistered: false
            },
            {
                id: 'summer-tour-player-2',
                name: '',
                golfId: '',
                teeType: '',
                dinnerOption: '',
                participating: singleRegistration === false,
                alreadyRegistered: false
            }
        ]

        setPlayers(summerTourPlayers);
    }

    const validateRegistration = () => {

        players.map(p => {
            p.name = p.name?.trim();
            p.email = p.email?.trim();
            p.handicap = p.handicap?.trim();
            return p;
        });

        if (players.filter(p => p.participating === true && p.alreadyRegistered === false).length === 0) {
            setValidationError('Du måste välja vilka spelare som skall vara med');
        } else if ([...new Set(players.map(item => item.name))].length < players.length) {
            setValidationError('Du måste ange unika namn på alla spelare');
        } else if (players.filter(p => p.participating === true && p.alreadyRegistered === false && (p.handicap === '' || p.handicap === undefined)).length > 0) {
            setValidationError('Du måste ange HCP för alla spelare');
        } else if (players.filter(p => p.participating === true && p.alreadyRegistered === false && (p.teeType === '' || p.teeType === undefined)).length > 0) {
            setValidationError('Du måste ange tee för alla spelare');
        } else if (players.filter(p => p.participating === true && p.alreadyRegistered === false && isEmailValid(p.email) === false).length > 0) {
            setValidationError('Ogiltig epost');
        } else if (players.filter(p => p.participating === true && p.alreadyRegistered === false && isHandicapValid(p.handicap) === false).length > 0) {
            setValidationError('Ogiltigt handikap');
        } else {
            setValidationError('');

            const registrations = [];
            players.filter(p => p.participating === true && p.alreadyRegistered === false).map(p => {

                //This is need for the SHCP calculation to work properly
                if (p.handicap.indexOf(',') + p.handicap.indexOf('.') === -2) {
                    p.handicap = p.handicap + ',0';
                }

                let registration = {
                    ...p,
                    competitionId: competition.id,
                    memberNo: member.number,
                    comment: registrationDetails.comment,
                    email: member.email,
                    telephone: registrationDetails.telephone
                };

                registrations.push(registration);

                return [];
            })

            setSubmitting(true);
            submitRegistration(registrations);
        }
    }

    const validateSummerTourRegistration = () => {

        players.map(p => {
            p.name = p.name?.trim();
            p.golfId = p.golfId?.trim();
            return p;
        });

        registrationDetails.email = registrationDetails.email?.trim();
        registrationDetails.telephone = registrationDetails.telephone?.trim();
        registrationDetails.postCode = registrationDetails.postCode?.trim();
        registrationDetails.city = registrationDetails.city?.trim();

        if (players.filter(p => p.participating === true && p.alreadyRegistered === false).length === 0) {
            setValidationError('Du måste välja vilka spelare som skall vara med');
        } else if (players.filter(p => p.participating === true).length > 2) {
            setValidationError('Du får bara anmäla max två spelare i taget');
        } else if (players.filter(p => p.participating === true && p.alreadyRegistered === false && (p.name === '' || p.name === undefined)).length > 0) {
            setValidationError('Du måste ange namn för alla spelare');
        } else if ([...new Set(players.map(item => item.name))].length < players.length) {
            setValidationError('Du måste ange unika namn på alla spelare');
        } else if (players.filter(p => p.participating === true && p.alreadyRegistered === false && (p.golfId === '' || p.golfId === undefined)).length > 0) {
            setValidationError('Du måste ange golfid för alla spelare');
        } else if (players.filter(p => p.participating === true && p.alreadyRegistered === false && isGolfIdValid(p.golfId) === false).length > 0) {
            setValidationError('Ogiltigt golfid, skriv på formatet ÅÅMMDD-XXX');
        } else if (players.filter(p => p.participating === true && p.alreadyRegistered === false && (p.teeType === '' || p.teeType === undefined)).length > 0) {
            setValidationError('Du måste ange tee för alla spelare');
        } else if (players.filter(p => p.participating === true && p.alreadyRegistered === false && (p.dinnerOption === '' || p.dinnerOption === undefined)).length > 0) {
            setValidationError('Du måste ange om man skall delta på middagen för alla spelare');
        } else if (registrationDetails.email === '' || registrationDetails.email === undefined) {
            setValidationError('Du måste ange e-post');
        } else if (isEmailValid(registrationDetails.email) === false) {
            setValidationError('Ogiltig ange epost');
        } else if (registrationDetails.telephone === '' || registrationDetails.telephone === undefined) {
            setValidationError('Du måste ange telefonnummer');
        } else if (registrationDetails.postCode === '' || registrationDetails.postCode === undefined) {
            setValidationError('Du måste ange postnummer');
        } else if (isPostCodeValid(registrationDetails.postCode) === false) {
            setValidationError('Ogiltigt postnummer, skriv på formatet XXXXX');
        } else if (registrationDetails.city === '' || registrationDetails.city === undefined) {
            setValidationError('Du måste ange ort');
        } else {
            setValidationError('');

            const registrations = [];
            players.filter(p => p.participating === true && p.alreadyRegistered === false).map(p => {

                let registration = {
                    ...p,
                    dinner: p.dinnerOption === '1',
                    competitionId: competition.id,
                    memberNo: member.number,
                    comment: registrationDetails.comment,
                    telephone: registrationDetails.telephone,
                    email: registrationDetails.email,
                    postCode: registrationDetails.postCode,
                    city: registrationDetails.city
                };

                registrations.push(registration);

                return [];
            });

            setSubmitting(true);
            submitRegistration(registrations);
        }
    }

    const submitRegistration = (registrations) => {
        let recaptchaToken = '';
        if (reCAPTCHAActive === true) {
            recaptchaToken = recaptchaRef.current.getValue();
        }

        GoogleSheetsService.submitRegistrations(competitionType, registrations, recaptchaToken).then((result) => {
            setSubmitting(false);
            setShowThankYou(result);
        });
    }

    const updatePlayer = (player) => {
        if (competitionType === 2) {
            //Check main list
            player.alreadyRegistered = registeredPlayers.find(p =>
                p.golfId === player.golfId &&
                isGolfIdValid(p.golfId) === true) !== undefined;

            //Check current register list
            player.alreadyRegistered = player.alreadyRegistered ||
                players.filter(p => p.participating === true).find(p =>
                    p.golfId === player.golfId &&
                    isGolfIdValid(p.golfId) === true &&
                    p.alreadyRegistered === false &&
                    p.id !== player.id) !== undefined;
        }
        const updatedPlayers = [...players];
        const index = updatedPlayers.findIndex(_ => _.id === player.id);
        updatedPlayers.splice(index, 1, player);
        setPlayers(updatedPlayers);
    }

    const getCompetitionStatus = () => {
        if (loadingCompetitions === true) {
            return "Hämtar tävlingar, vänligen vänta lite...";
        } else if (loadingCompetitions === false && competitions && competitions.length > 0) {
            return "Välj tävling";
        } else {
            return "Det finns inga tävlingar att anmäla sig till just nu";
        }
    }

    const getCompetitionSelectName = (competition) => {
        let name = '';
        if (competitionType === 1) {
            name = `${competition.track} - ${competition.name}, ${competition.startingTime.split('T')[0]} prel. starttid kl. ${competition.startingTime.split('T')[1].substring(0, 5)}`;
            if (competition.noOfOpenSpots < 7) {
                name = `${name} (${getSpotDescription(competition)})`;
            }
        } else {
            name = `${competition.sheetName}`;
        }

        return name;
    }

    const getSpotDescription = (competition) => {
        if (competition.noOfOpenSpots === 0) {
            return `Fullbokad, anmäl reservplats`;
        } else if (competition.noOfOpenSpots < 7 && competition.noOfOpenSpots > 1) {
            return `Endast ${competition.noOfOpenSpots} platser kvar`;
        } else if (competition.noOfOpenSpots === 1) {
            return `Endast ${competition.noOfOpenSpots} plats kvar`;
        }
    }

    if (showThankYou > 0) {
        return (
            <React.Fragment>
                <div ref={thankYouRef}></div>
                <ThankYou competitionType={competitionType} competition={competition} players={players.filter(p => p.participating === true && p.alreadyRegistered === false)} member={member} registrationDetails={registrationDetails} state={showThankYou} newBooking={() => {
                    setCompetition({ id: '' });
                    setMember({});
                    setPlayers([]);
                    setShowThankYou(0);
                    setRegistrationDetails({
                        email: '',
                        telephone: '',
                        postCode: '',
                        city: ''
                    });
                    setValidationWarning('');
                    setSingleRegistration(false);
                    setApproveReCAPTCHA(false);
                    setApproveGDPR(false);
                }} />
            </React.Fragment>)
    } else {
        return (
            <div className="s4g-reg-form">
                {goBack === true && <div className='s4g-section'>
                    <LoadingButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={resetRegistrationType}>
                        Gå tillbaka
                    </LoadingButton>
                </div>}
                <div className='s4g-section'>
                    {competitionType === 1 && <span className='s4g-comp-title'>Välj tävling och följ sedan instruktionerna för att anmäla er till tävlingen</span>}
                    {competitionType === 1 && <p>OBS! Du behöver <b>kundnummer</b> och <b>HCP</b> tillgängligt</p>}
                    {competitionType === 2 && <p className='s4g-comp-title'>Välj sommartours-tävlingen och följ sedan instruktionerna för att anmäla er till tävlingen</p>}
                    <FormControl fullWidth ref={competitionRef}>
                        <InputLabel id="pick-competition">{getCompetitionStatus()}</InputLabel>
                        <Select
                            labelId="pick-competition"
                            id="pick-competition-select"
                            disabled={loadingCompetitions === true || competitions.length === 0}
                            label={getCompetitionStatus()}
                            value={competition.id}
                            onChange={e => {
                                let updatedPlayers = [...players];
                                updatedPlayers.map(p => p.participating = false);
                                setPlayers(updatedPlayers.sort((a, b) => sortPlayers(a, b)));
                                let competition = competitions.find(c => c.id === e.target.value);
                                competition.description = competition.description.replace(/(?:\r\n|\r|\n)/g, '<br/>');
                                setCompetition(competition);

                                if (competitionType === 1) {
                                    setPlayers(updatedPlayers.sort((a, b) => (a.name > b.name) ? 1 : -1));
                                } else if (competitionType === 2) {
                                    setDefaultSummerTourPlayers();
                                }

                                setRegisteredPlayers([]);
                                GoogleSheetsService.getPlayers(competitionType, competition.sheetName).then((result) => {
                                    setRegisteredPlayers(result);
                                });
                            }}>
                            {competitions.map(function (competition) {
                                return (<MenuItem
                                    sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}
                                    key={competition.id}
                                    value={competition.id}>{getCompetitionSelectName(competition)}
                                </MenuItem>)
                            })}
                        </Select>
                    </FormControl>
                </div>
                {competition.id > 0 &&
                    <div>
                        <div className='s4g-section'>
                            <h2 ref={infoTitleRef}>{`${competition.track} - ${competition.name}`}</h2>
                            {competitionType === 1 && <p><b>Datum:</b> {`${competition.startingTime.split('T')[0]}, prel. starttid kl. ${competition.startingTime.split('T')[1].substring(0, 5)}`}</p>}
                            {competitionType === 2 && <p><b>Datum:</b> {`${competition.startingTime.split('T')[0]}`}</p>}
                            {competition.noOfOpenSpots < 7 && <p><b>Platser:</b> {getSpotDescription(competition)}</p>}
                            {competition.noOfOpenSpots >= 7 && <p><b>Platser:</b> {competition.noOfOpenSpots}</p>}
                            <p><b>Information om tävlingen: </b><br /><span dangerouslySetInnerHTML={{ __html: competition.description }} /></p>
                            {competition.link && <p>Mer information om banan: <a href={competition.link} target="_blank" rel="noopener noreferrer">{competition.link}</a></p>}
                            {competitionType === 1 &&
                                <React.Fragment>
                                    <p><b>Allmän information: </b></p>
                                    <ul>
                                        <li>
                                            Anmälan till tävling måste göras senast kl 10.00 två dagar innan tävling.
                                        </li>
                                        <li>
                                            Startlistorna publiceras senast kl 15.00 dagen innan tävling.
                                        </li>
                                        <li>
                                            Startavgift till tävling är 5€ per person och går oavkortat till prisbordet.
                                        </li>
                                        <li>
                                            <b>OBS!</b> Behöver ni golfbil bokar ni det själva med klubben så snart som möjligt.
                                        </li>
                                        <li>
                                            För att anmäla er till tävlingen: Fyll i kundnummer nedan och klicka på <b>Hämta medlem</b>
                                        </li>
                                    </ul>
                                    <PlayerList players={registeredPlayers.filter(_ => _.status === 1)} />
                                </React.Fragment>
                            }
                            {competitionType === 2 &&
                                <p>Om du är tidigare gäst hos Stay4Golf fyll i ditt kundnummer i rutan, klicka på "Hämta medlem", så fylls vissa fält i automatiskt. Annars måste Du fylla i alla fälten.</p>
                            }

                        </div>
                        <GetMemberForm updateMember={updateMember}
                            memberSearchRef={memberSearchRef}
                            required={competitionType === 1}
                            label={competitionType === 1 ? "Kundnummer" : "Kundnummer (om du har ett)"}
                            autoFocus={false} />
                    </div>
                }
                {players.length > 0 && <div className='s4g-section'>
                    <h4>Vilka skall vara med att spela?</h4>
                    {competitionType === 1 && <p>OBS! Tänk på att du kan anmäla fler spelare samtidigt</p>}
                    {competitionType === 1 && <div>
                        <p>
                            <i>Bekräftelsemail för bokningen kommer skickas till eposten <b>{member.email}</b> som är registrerad för kundnumret.</i>
                        </p>
                    </div>}
                    {member.players && <PlayerSelect players={players} updatePlayers={updatePlayers} playersRef={playersRef}
                        maxSelect={competitionType === 2 ? 2 : undefined}
                        canAddGuest={competitionType !== 2} />}
                    {!member.players && <div className='s4g-inline'>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox
                                checked={singleRegistration}
                                onChange={e => {
                                    let updatedPlayers = [...players];
                                    setSingleRegistration(e.target.checked);
                                    updatedPlayers[1].participating = !e.target.checked;
                                    setPlayers(updatedPlayers);
                                }}
                                inputProps={{ 'aria-label': 'controlled' }}
                            />} label='Jag vill bara anmäla en spelare' />
                        </FormGroup>
                    </div>}
                </div>}
                {players.filter(_ => _.participating === true).length > 0 && <React.Fragment>
                    <hr />
                    {players.map(function (player) {
                        if (competitionType === 1 && player.participating === true) {
                            if (player.alreadyRegistered === false) {
                                return (<PlayerForm player={player} updatePlayer={updatePlayer} key={player.id} isBlueTeeAvailable={competition.isBlueTeeAvailable} />);
                            } else {
                                return (<div key={player.id} className='s4g-section'>
                                    <h4 className='s4g-player-name'>{`${player.name} ${player.isGuest ? '(Gäst)' : ''} ${player.status === 2 ? '(Reserv)' : ''}`} är redan anmäld</h4>
                                </div>)
                            }
                        } else if (competitionType === 2 && player.participating === true) {
                            return (<PlayerSummerTourForm player={player} disableName={member.players !== undefined} disableGolfId={false} updatePlayer={updatePlayer} key={player.id} />);
                        } 

                        return null;
                    })}
                    <hr />
                </React.Fragment>}
                {players.filter(p => p.participating).length > 0 &&
                    <div>
                        <DetailsForm competitionType={competitionType} registrationDetails={registrationDetails} setRegistrationDetails={setRegistrationDetails} />
                        <ValidationFeedback validationWarning={validationWarning} validationError={validationError} />
                        {competitionType === 2 && <div className='s4g-section'>
                            <FormGroup>
                                <FormControlLabel control={<Checkbox
                                    checked={approveGDPR}
                                    onChange={e => setApproveGDPR(e.target.checked)}
                                    inputProps={{ 'aria-label': 'controlled' }}
                                />} label='Jag samtycker till att Stay4Golf sparar mina kontaktuppgifter för att kunna informera om befintliga och kommande evenemang' />
                            </FormGroup>
                        </div>}
                        {reCAPTCHAActive === true && <div className='s4g-section'>
                            <ReCAPTCHA ref={recaptchaRef} hl="sv" sitekey="6LdbqJIeAAAAALRadjMPe9Ac5J3bJcHY073llqAl" onChange={e => { setApproveReCAPTCHA(true) }} />
                        </div>}
                        <div className='s4g-section'>
                            <LoadingButton
                                loading={submitting}
                                disabled={
                                    (!approveGDPR && competitionType === 2) || // Måste ha godkännt hantering av personuppfiger för SummerTour
                                    (!approveReCAPTCHA && reCAPTCHAActive) ||  // Måste ha godkännt "Jag är ingen robot" OM detta är aktivt
                                    players.filter(p => p.participating === true && p.alreadyRegistered === false).length === 0} // Måste ha valt spelare
                                variant="contained"
                                loadingPosition="start"
                                ref={submitButtonRef}
                                startIcon={<SaveIcon />}
                                onClick={e => {
                                    if (competitionType === 1) {
                                        validateRegistration();
                                    } else if (competitionType === 2) {
                                        validateSummerTourRegistration();
                                    }
                                }}>
                                {submitting ? "Skickar anmälan" : "Skicka in anmälan"}
                            </LoadingButton>
                        </div>
                    </div>
                }
            </div >
        );
    }
}

export default RegistrationForm;
